/**
 * useLightsCanvas: the draw loop of the lights view (plan sections 3 "Motion", 7 and 12).
 *
 * Frame state lives in a ref, never in React state: brightness and camera approach their targets once per frame at 30 fps
 * (FRAME_INTERVAL_MS), DPR capped at MAX_DPR, and the rAF loop stops as soon as nothing moves, the tab is hidden or the
 * canvas left the document. A new `lights` (one per diag_state) re-aims the auto camera; wheel / drag / keys / fly-to take
 * manual control until the next one. Reduced motion jumps instead of easing and drops the pulse. The theme palette is read
 * from the stage element's CSS custom properties whenever `themeMode` changes.
 *
 * `selection` is {node: nid} | {edge: id} | null and `onSelect(selection)` is called on a click (empty space clears).
 * Returns {hover: {node | edge, x, y} | null, recentre(), zoomBy(factor), flyTo(nid | {x, y}), onKeyDown}.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { clampScale, fitBox, panBy, stepCamera, targetCamera, wheelFactor, zoomAt } from "./lightsCamera";
import { CAMERA, FRAME_INTERVAL_MS, LIGHT_LERP, LIGHT_SETTLE_EPS, MAX_DPR, SELECTION, TONES } from "./lightsConstants";
import { drawFrame, hitTest, hitTestEdge } from "./lightsRenderer";
import { litOrder } from "./lightsState";
import { readTheme } from "./lightsTheme";

const ZOOM_KEYS = Object.freeze({ "+": CAMERA.KEY_ZOOM, "=": CAMERA.KEY_ZOOM, "-": 1 / CAMERA.KEY_ZOOM, _: 1 / CAMERA.KEY_ZOOM });
const PAN_KEYS = Object.freeze({
  ArrowLeft: [CAMERA.KEY_PAN_PX, 0], ArrowRight: [-CAMERA.KEY_PAN_PX, 0], ArrowUp: [0, CAMERA.KEY_PAN_PX], ArrowDown: [0, -CAMERA.KEY_PAN_PX],
});
const RECENTRE_KEY = "0";
const STEP_KEYS = Object.freeze({ "]": 1, "[": -1 });
const SELECT_KEY = "Enter";
const DRAG_THRESHOLD_PX = 3;

const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

/** One frame of brightness approach; returns true when every light reached its target. */
function stepLights(display, lights, t) {
  let settled = true;
  lights.nodes.forEach((n) => {
    const cur = display.get(n.nid) ?? 0;
    const next = cur + (n.lit - cur) * t;
    if (Math.abs(n.lit - next) > LIGHT_SETTLE_EPS) settled = false;
    display.set(n.nid, Math.abs(n.lit - next) > LIGHT_SETTLE_EPS ? next : n.lit);
  });
  return settled;
}

const hasPulse = (lights) => [...lights.nodes.values()].some((n) => n.tone === TONES.EVALUATING);
const fitScaleOf = (s) => fitBox(s.layout.bounds, s.view).scale;
const isReady = (s) => !!(s.layout && s.lights && s.view.width > 0 && s.view.height > 0);
const frameOf = (s) => ({
  view: s.view, cam: s.cam, fitScale: fitScaleOf(s), layout: s.layout, lights: s.lights, display: s.display,
  time: now(), reducedMotion: s.reducedMotion, hoverId: s.externalHoverId || s.hoverId, hoverEdgeId: s.hoverEdgeId,
  selectedId: s.selection?.node || null, selectedEdgeId: s.selection?.edge || null, theme: s.theme, options: s.options,
});

/** The node or edge under screen (sx, sy): nodes take priority over edges. */
function pick(s, sx, sy) {
  if (!isReady(s) || !s.cam) return null;
  const f = frameOf(s);
  const node = hitTest(f, sx, sy);
  if (node) return { node };
  const edge = hitTestEdge(f, sx, sy);
  return edge ? { edge } : null;
}

export default function useLightsCanvas(canvasRef, { layout, lights, reducedMotion, options, themeMode, selection, hoverId, onSelect }) {
  const [hover, setHover] = useState(null);
  const s = useRef({
    view: { width: 0, height: 0 }, cam: null, target: null, auto: true, display: new Map(),
    raf: null, last: 0, drag: null, hoverId: null, hoverEdgeId: null, theme: readTheme(null, themeMode),
  }).current;
  s.layout = layout;
  s.lights = lights;
  s.reducedMotion = reducedMotion;
  s.options = options;
  s.selection = selection || null;
  s.externalHoverId = hoverId || null;
  s.onSelect = onSelect;

  const tick = useCallback((time) => {
    s.raf = null;
    const canvas = canvasRef.current;
    if (!canvas || !canvas.isConnected || (typeof document !== "undefined" && document.hidden) || !isReady(s)) return;
    if (time - s.last < FRAME_INTERVAL_MS) { s.raf = requestAnimationFrame(tick); return; }
    s.last = time;
    const jump = s.reducedMotion ? 1 : null;
    const lightsSettled = stepLights(s.display, s.lights, jump || LIGHT_LERP);
    let camSettled = true;
    if (s.target) [s.cam, camSettled] = stepCamera(s.cam || s.target, s.target, jump || CAMERA.LERP);
    const ctx = canvas.getContext("2d");
    if (ctx) drawFrame(ctx, frameOf(s));
    if (!lightsSettled || !camSettled || (!s.reducedMotion && hasPulse(s.lights))) s.raf = requestAnimationFrame(tick);
  }, [canvasRef, s]);

  const wake = useCallback(() => {
    if (s.raf == null && typeof requestAnimationFrame === "function") s.raf = requestAnimationFrame(tick);
  }, [s, tick]);

  const aim = useCallback(() => {
    if (!isReady(s)) return;
    s.auto = true;
    s.target = targetCamera(s.layout, s.lights.focus, s.view);
    if (!s.cam) s.cam = fitBox(s.layout.bounds, s.view);
    wake();
  }, [s, wake]);

  // A new bundle: lights start from dark at the whole-category frame.
  useEffect(() => {
    s.display = new Map();
    s.cam = null;
    aim();
  }, [layout, s, aim]);

  // Every new diag_state re-aims the auto camera (owner default: re-frame per turn).
  useEffect(() => { aim(); }, [lights, aim]);

  // Options, selection, external hover or reduced motion changed: one more frame.
  useEffect(() => { wake(); }, [reducedMotion, options, selection, hoverId, wake]);

  // The palette comes from the stage's CSS custom properties (graphLights.css per theme).
  useEffect(() => {
    s.theme = readTheme(canvasRef.current?.parentElement || null, themeMode);
    wake();
  }, [canvasRef, s, themeMode, wake]);

  // Size: CSS pixels from the parent box, backing store at min(devicePixelRatio, MAX_DPR).
  useEffect(() => {
    const canvas = canvasRef.current;
    const box = canvas?.parentElement;
    if (!canvas || !box || typeof ResizeObserver === "undefined") return undefined;
    const resize = () => {
      const { width, height } = box.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      canvas.width = Math.max(1, Math.round(width * dpr));
      canvas.height = Math.max(1, Math.round(height * dpr));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      canvas.getContext("2d")?.setTransform(dpr, 0, 0, dpr, 0, 0);
      s.view = { width, height };
      if (s.auto || !s.cam) { s.cam = null; aim(); } else wake();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(box);
    resize();
    const onVisible = () => { if (!document.hidden) wake(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", onVisible);
      if (s.raf != null) cancelAnimationFrame(s.raf);
      s.raf = null;
    };
  }, [canvasRef, s, aim, wake]);

  const takeControl = useCallback((cam) => {
    s.auto = false;
    s.cam = cam;
    s.target = cam;
    wake();
  }, [s, wake]);

  /** Manual zoom about the canvas centre (header / overlay buttons and +/- keys). */
  const zoomBy = useCallback((factor) => {
    if (!s.cam) return;
    takeControl({ ...s.cam, scale: clampScale(s.cam.scale * factor, fitScaleOf(s)) });
  }, [s, takeControl]);

  /** Eases the camera onto a node (by id) or a world point, zooming in to at least FLY_SCALE_OVER_FIT; manual until the next turn. */
  const flyTo = useCallback((target) => {
    if (!target || !isReady(s)) return;
    const p = typeof target === "string" ? s.layout.positions.get(target) : target;
    if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.y)) return;
    const fit = fitScaleOf(s);
    const scale = clampScale(Math.max(s.cam ? s.cam.scale : fit, fit * SELECTION.FLY_SCALE_OVER_FIT), fit);
    s.auto = false;
    if (!s.cam) s.cam = fitBox(s.layout.bounds, s.view);
    s.target = { x: p.x, y: p.y, scale };
    wake();
  }, [s, wake]);

  const updateHover = useCallback((sx, sy) => {
    const hit = pick(s, sx, sy);
    const id = hit?.node ? hit.node.nid : null;
    const edgeId = hit?.edge ? hit.edge.id : null;
    if (id !== s.hoverId || edgeId !== s.hoverEdgeId) { s.hoverId = id; s.hoverEdgeId = edgeId; wake(); }
    setHover((prev) => (hit ? { ...hit, x: sx, y: sy } : prev ? null : prev));
  }, [s, wake]);

  // Pointer: wheel zooms at the cursor, drag pans, click selects (empty space clears), double-click recentres on the lit set.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const local = (e) => { const r = canvas.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
    const onWheel = (e) => {
      if (!s.cam) return;
      e.preventDefault();
      const [sx, sy] = local(e);
      takeControl(zoomAt(s.cam, s.view, wheelFactor(e.deltaY), sx, sy, fitScaleOf(s)));
    };
    const onDown = (e) => { if (e.button === 0) s.drag = { x: e.clientX, y: e.clientY, moved: false }; };
    const onMove = (e) => {
      const [sx, sy] = local(e);
      if (!s.drag || !s.cam) { updateHover(sx, sy); return; }
      const dx = e.clientX - s.drag.x;
      const dy = e.clientY - s.drag.y;
      if (!s.drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      if (!s.drag.moved) canvas.setPointerCapture?.(e.pointerId);
      s.drag = { x: e.clientX, y: e.clientY, moved: true };
      takeControl(panBy(s.cam, dx, dy));
    };
    const onUp = (e) => {
      const click = s.drag && !s.drag.moved;
      s.drag = null;
      canvas.releasePointerCapture?.(e.pointerId);
      if (!click || typeof s.onSelect !== "function") return;
      const [sx, sy] = local(e);
      const hit = pick(s, sx, sy);
      s.onSelect(hit ? (hit.node ? { node: hit.node.nid } : { edge: hit.edge.id }) : null);
    };
    const onLeave = () => { if (!s.drag) { s.hoverId = null; s.hoverEdgeId = null; setHover(null); wake(); } };
    canvas.addEventListener("wheel", onWheel, { passive: false });
    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("pointercancel", onUp);
    canvas.addEventListener("pointerleave", onLeave);
    canvas.addEventListener("dblclick", aim);
    return () => {
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("pointercancel", onUp);
      canvas.removeEventListener("pointerleave", onLeave);
      canvas.removeEventListener("dblclick", aim);
    };
  }, [canvasRef, s, aim, takeControl, updateHover, wake]);

  /** Keyboard on the focused canvas: +/- zoom, arrows pan, 0 recentres, [ ] step through the lit nodes, Enter selects the hovered one. */
  const onKeyDown = useCallback((e) => {
    if (!s.cam) return;
    if (e.key === RECENTRE_KEY) { e.preventDefault(); aim(); return; }
    const factor = ZOOM_KEYS[e.key];
    if (factor) { e.preventDefault(); zoomBy(factor); return; }
    const pan = PAN_KEYS[e.key];
    if (pan) { e.preventDefault(); takeControl(panBy(s.cam, pan[0], pan[1])); return; }
    const step = STEP_KEYS[e.key];
    if (step && typeof s.onSelect === "function") {
      e.preventDefault();
      const order = litOrder(s.lights);
      if (!order.length) return;
      const at = order.indexOf(s.selection?.node || null);
      const next = order[(at + step + order.length) % order.length];
      s.onSelect({ node: next });
      flyTo(next);
      return;
    }
    if (e.key !== SELECT_KEY || typeof s.onSelect !== "function") return;
    if (s.hoverId) { e.preventDefault(); s.onSelect({ node: s.hoverId }); } else if (s.hoverEdgeId) { e.preventDefault(); s.onSelect({ edge: s.hoverEdgeId }); }
  }, [s, aim, zoomBy, takeControl, flyTo]);

  return { hover, recentre: aim, zoomBy, flyTo, onKeyDown };
}
